import moment from "moment";
import parse from "html-react-parser";
import * as ExcelJS from "exceljs";
import { AgTechQueryParam } from "../enums/AgTechQueryParams.enum";
import { AppConstants } from "../constants";
import APPCONFIG from "../config/app.config";
import { themeColors } from "../colors";
import { IFilterQueryParams, IQueryParam, ISortQueryParams } from "./types";
import {
  AttributeDatatype,
  ATTRIBUTE_MASTER_CATALOG_PREFIX_MAP,
  ColumnType,
  FieldsType,
  MasterCatalogAllowedRoles,
  OdQueryParam,
  SortType,
  UserRoles,
  TrialTypeEnum,
} from "../enums";
import { IPermission, IProduct, ITradeName, IUserGroup, IApplicationAccessibility } from "../models";

export const helperFunctions = {
  getPageTitle(path: string): string {
    const page = AppConstants.pages.find((page) => page.path === path);
    return page ? page.title : "Master Data";
  },

  getLOLToken(): string | null {
    const token = localStorage.getItem(AppConstants.SSO_AUTH_TOKEN_DETAIL);
    return token ? JSON.parse(token)?.access_token : null;
  },

  getAuthTokenDetails(): string | null {
    const tokenDetails = localStorage.getItem(
      AppConstants.SSO_AUTH_TOKEN_DETAIL,
    );
    return tokenDetails ? JSON.parse(tokenDetails) : null;
  },

  getKioskModeValue(): boolean {
    const appMode = localStorage.getItem(AppConstants.APP_MODE);
    return appMode?.toLowerCase() === AppConstants.KIOSK.toLowerCase();
  },

  getUserNameFromUPN(upn?: string | null): string | null {
    return upn?.split("@")[0] ?? null;
  },

  getAPIKey(): string {
    return APPCONFIG.apiKey ?? "";
  },

  addQueryParam(
    url: string,
    key: AgTechQueryParam | OdQueryParam,
    values: string[] | number[] | boolean[],
  ): string {
    return url.indexOf("?") > 0
      ? `${url}&${key}=${values.join(",")}`
      : `${url}?${key}=${values.join(",")}`;
  },

  updatePatchRequestParameter(
    path: string,
    value: any,
    operationType: string = "replace",
  ) {
    return { op: operationType, path: "/" + path, value: value };
  },

  getSeasonFromYear() {
    const date = new Date();
    const currentYear = date.getFullYear();
    return currentYear - 30;
  },

  getCurrentSeason() {
    const date = new Date();
    let currentSeason;
    const currentYear = date.getFullYear();
    // const seasonDate = new Date(currentYear, 10, 1);
    // if (date >= seasonDate) {
    //   currentSeason = currentYear + 1;
    // } else {
    // currentSeason = currentYear;
    //}

    currentSeason = currentYear + 1;
    return currentSeason;
  },

  getErrorMessage(errorRes: any) {
    const { errors, message } = errorRes ?? {};
    if (errors?.length > 0) {
      return errors?.[0]?.message;
    }
    return message ?? "Something went wrong!";
  },

  isDashboard(path: string) {
    return path === "/main/dashboard";
  },
  isHideCrops(path: string) {
    if (!path) return;
    return (
      path?.toLowerCase()?.includes("/onfarm-reports") ||
      path?.toLowerCase()?.includes("/createlocation") ||
      path?.toLowerCase()?.includes("/report") ||
      path?.toLowerCase()?.includes("/regions") ||
      // path?.toLowerCase()?.includes("/createlocation") ||
      path?.toLowerCase()?.includes("mastercatalog") ||
      path?.toLowerCase()?.includes("fieldbookcatalog") ||
      path?.toLowerCase()?.includes("repbookcatalog") ||
      path?.toLowerCase()?.includes("labbookcatalog") ||
      path?.toLowerCase()?.includes("applicationscatalog") ||
      path?.toLowerCase()?.includes("observationscatalog")
    );
  },
  isHideSeason(path: string) {
    if (!path) return;
    return (
      path?.toLowerCase()?.includes("mastercatalog") ||
      path?.toLowerCase()?.includes("fieldbookcatalog") ||
      path?.toLowerCase()?.includes("repbookcatalog") ||
      path?.toLowerCase()?.includes("labbookcatalog") ||
      path?.toLowerCase()?.includes("applicationscatalog") ||
      path?.toLowerCase()?.includes("observationscatalog")
    );
  },
  hasChangesMasterCatalog(
    initialState: any,
    currentState: {
      filters: { crops?: any[]; season?: any[] };
      selectedTeams: Set<string> | undefined;
      selectedStates: Set<string> | undefined;
      selectedLocations: Set<number> | undefined;
      selectedTrials: number[] | undefined;
      analysisStatusSelected: string[] | undefined;
      teamSearch?: string;
      stateSearch?: string;
      locationSearch?: string;
      trialSearch?: string;
      isTrialPriority?: boolean;
      plotType?: string;
    },
  ) {
    const {
      filters,
      selectedTeams,
      selectedStates,
      selectedLocations,
      selectedTrials,
      analysisStatusSelected,
      teamSearch,
      stateSearch,
      locationSearch,
      trialSearch,
      isTrialPriority,
      plotType,
    } = currentState || {};

    return (
      (filters?.crops?.length ?? 0) !==
      (initialState?.filters?.crops?.length ?? 0) ||
      (filters?.season?.length ?? 0) !==
      (initialState?.filters?.season?.length ?? 0) ||
      (selectedTeams?.size ?? 0) !== (initialState?.selectedTeams?.size ?? 0) ||
      (selectedStates?.size ?? 0) !==
      (initialState?.selectedStates?.size ?? 0) ||
      (selectedLocations?.size ?? 0) !==
      (initialState?.selectedLocations?.size ?? 0) ||
      (selectedTrials?.length ?? 0) !==
      (initialState?.selectedTrials?.length ?? 0) ||
      (analysisStatusSelected?.length ?? 0) !==
      (initialState?.analysisStatusSelected?.length ?? 0) ||
      (teamSearch?.trim() ?? "") !== (initialState?.teamSearch ?? "") ||
      (stateSearch?.trim() ?? "") !== (initialState?.stateSearch ?? "") ||
      (locationSearch?.trim() ?? "") !== (initialState?.locationSearch ?? "") ||
      (trialSearch?.trim() ?? "") !== (initialState?.trialSearch ?? "") ||
      (isTrialPriority ?? false) !== (initialState?.isTrialPriority ?? false) ||
      (plotType ?? TrialTypeEnum.RESEARCH) !==
      (initialState?.plotType ?? TrialTypeEnum.RESEARCH)
    );
  },
  removeBeforeColon(str: string) {
    // Check if there's a colon in the string
    const colonIndex = str.indexOf(":");
    // If a colon exists, return the substring after the colon
    if (colonIndex !== -1) {
      return str.substring(colonIndex + 1).trim(); // Trim any spaces after the colon
    }
    // If no colon exists, return the original string
    return str;
  },

  generateRandomString() {
    const dec2hex = (dec: any) => {
      return ("0" + dec.toString(16)).slice(-2);
    };
    const array = new Uint32Array(56 / 2);
    window.crypto.getRandomValues(array);
    return Array.from(array, dec2hex).join("");
  },

  base64urlencode(a: any) {
    var str = "";
    var bytes = new Uint8Array(a);
    var len = bytes.byteLength;
    for (var i = 0; i < len; i++) {
      str += String.fromCharCode(bytes[i]);
    }
    return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  },

  normalizeToArray(value: any): any[] {
    if (Array.isArray(value)) return value;
    if (typeof value === "string") return [value];
    return [];
  },

  sha256(plain: any) {
    // returns promise ArrayBuffer
    const encoder = new TextEncoder();
    const data = encoder.encode(plain);
    return window.crypto.subtle.digest("SHA-256", data);
  },
  capitalizeFirstLetter(str: string) {
    if (!str) return "";
    return str[0].toUpperCase() + str.slice(1);
  },
  showYesNo(value: string) {
    return value === "true" ? "Yes" : value === "false" ? "No" : "";
  },
  mapBooleanValues(value: string) {
    if (!value) return;
    return value?.toLowerCase() === "yes" ? "true" : "false";
  },

  isBackButtonVisible(pathname: string) {
    return ["main/trial", "main/mastercatalog", "/main/trial-experiments", "/main/trial-experiments"].some(path =>
      pathname.includes(path)
    ) ? 0 : 1;
  },

  isHideHeader(pathName: string) {
    if (!pathName) return;
    return (
      pathName?.toLowerCase()?.includes("/fieldmap") ||
      pathName?.toLowerCase()?.includes("/datacollection")
    );
  },
  isActive(status: string) {
    if (!status) return false;
    return (
      status.toLowerCase() === "active" || status.toLowerCase() === "locked"
    );
  },
  isLocked(status: string) {
    if (!status) return false;
    return status.toLowerCase() === AgTechQueryParam.Locked?.toLowerCase();
  },

  triggerCustomEvent(eventType: string, data?: any) {
    const event = new CustomEvent(eventType, { detail: data });
    document.dispatchEvent(event);
  },
  isAuthenticated() {
    return localStorage.getItem("SSO_AUTH_TOKEN_DETAIL");
  },
  getCropColors(cropName: string) {
    switch (cropName) {
      case "corn":
        return {
          borderColor: `${themeColors.inputsBorder.Corn}`,
          backgroundColor: "rgba(223, 217, 82, 0.4)",
          boxShadowColor: "2px 4px 8px rgba(150, 100, 0, 0.5)",
        };
      case "soybeans":
        return {
          borderColor: `${themeColors.inputsBorder.SoyaBeans}`,
          backgroundColor: "rgba(106, 159, 89, 0.2)",
          boxShadowColor: "4px 4px 10px rgba(64, 64, 0, 0.5)",
        };
      case "canola":
        return {
          borderColor: `${themeColors.inputsBorder.Canola}`,
          backgroundColor: "rgba(237 212 74 / 20%)",
          boxShadowColor: "2px 4px 8px rgba(90, 60, 0, 0.5);",
        };
      case "smallgrains":
        return {
          borderColor: `${themeColors.inputsBorder.SmallGrains}`,
          backgroundColor: "rgba(210, 180, 140, 0.2)",
          boxShadowColor: "4px 4px 10px rgba(160, 120, 80, 0.5)",
        };
      case "sunflower":
        return {
          borderColor: `${themeColors.inputsBorder.Sunflower}`,
          backgroundColor: "rgba(241 132 16 / 20%);",
          boxShadowColor: "4px 4px 10px rgba(170, 70, 0, 0.5)",
        };
      case "pulse":
        return {
          borderColor: `${themeColors.inputsBorder.Pulses}`, // You need to define this in your theme
          backgroundColor: "rgba(200,91,5, 0.1)", // light green/brown
          boxShadowColor: "4px 4px 10px rgba(90, 110, 50, 0.5)", // earthy green
        };
      case "sorghum":
        return {
          borderColor: `${themeColors.inputsBorder.Sorghum}`, // Define in your theme
          backgroundColor: "rgba(194, 154, 95, 0.2)", // warm tan/light brown
          boxShadowColor: "4px 4px 10px rgba(140, 100, 50, 0.5)", // soft brown shadow
        };
      default:
        return {
          borderColor: `${themeColors.inputsBorder.LightBlue}`,
          backgroundColor: "",
          boxShadowColor: "0px 4px 4px 0px #00000014",
        };
    }
  },

  getFilterDropdownOptions(options: any[], labelKey: string, valueKey: string) {
    return (
      options?.map((item) => ({
        label: item[labelKey] ?? item,
        value: item[valueKey] ?? item,
      })) || []
    );
  },

  getFiltersDefaultValue(filters: any[]) {
    return filters.map((item) => {
      const hasValidValue =
        item?.value !== undefined && item?.value !== null && item?.value !== "";
      return {
        ...item,
        value: hasValidValue ? item.value : this.getDefaultNullValue(item),
      };
    });
  },

  getFilterQueryParams(appliedFilters: any[]): IQueryParam[] {
    const queryParams: IQueryParam[] = [];

    if (appliedFilters?.length === 0) return queryParams;

    for (const item of appliedFilters) {
      if (item.dataKey === "rm") {
        if (item.minRM > 0) {
          queryParams.push({
            key: AgTechQueryParam.minRM,
            value: [item.minRM.toString()],
          });
        }
        if (item.maxRM < 366) {
          queryParams.push({
            key: AgTechQueryParam.maxRM,
            value: [item.maxRM.toString()],
          });
        }
      } else {
        const filterKey = item.queryParamsKey?.[0] ?? item.dataKey;
        if (Array.isArray(item.value) && item.value.length > 0) {
          for (let filterValues of item.value) {
            queryParams.push({
              key: filterKey as AgTechQueryParam,
              value: [encodeURIComponent(filterValues)],
            });
          }
        } else {
          queryParams.push({
            key: filterKey as AgTechQueryParam,
            value: [encodeURIComponent(item?.value)],
          });
        }
      }
    }

    return queryParams;
  },
  getProductTreatmentName(tradeName: ITradeName[]) {
    if (!Array.isArray(tradeName)) return "";
    return Array.isArray(tradeName)
      ? tradeName
        .map((trade) => {
          return trade?.isUntreated
            ? `${trade.name || ""}`
            : `${trade.name || ""} @ ${trade.quantity || ""} ${trade.unit || ""
            }/${trade.area}`;
        })
        .join(" | ")
      : "";
  },
  getTradeDetails(tradeInfo: ITradeName[]) {
    if (!Array.isArray(tradeInfo)) return "";
    const htmlString = tradeInfo
      .map((trade) => {
        const details = [
          trade.name && `<b>Trade Name - </b> ${trade.name}`,
          trade.ai && `<b>AI - </b> ${trade.ai}`,
          trade.epa && `<b>EPA# - </b> ${trade.epa}`,
          trade.rei && `<b>REI - </b> ${trade.rei}`,
          (trade.quantity || trade.unit || trade.area) &&
          (() => {
            const rate = `${trade.quantity ?? ""} ${trade.unit ?? ""}${trade.area ? "/" + trade.area : ""
              }`.trim();
            return rate && rate !== "/" ? `<b>Rate - </b> ${rate}` : null;
          })(),
        ].filter(Boolean);
        return details.join(", ");
      })
      .join(" | ");
    return parse(htmlString);
  },
  rateFormattedValue(products: any, index: number) {
    const product = products?.[index ?? 0];
    if (!product) return "";

    return (
      (product.quantity ?? "") +
      " " +
      (product.unit ?? "") +
      (product.area ? "/" + product.area : "")
    );
  },
  getUserPermissions(moduleNameKey: string, userGroup: IUserGroup) {
    const permissions = userGroup?.permissions?.filter(
      (permission: IPermission) =>
        permission?.name?.toLowerCase() === moduleNameKey.toLowerCase(),
    )?.[0];
    return (
      permissions ?? {
        canAdd: false,
        canEdit: false,
        canView: false,
        canDelete: false,
      }
    );
  },

  /**
   * Checks if a user group has access to a specific application
   * @param applicationName - The name of the application to check access for
   * @param userGroup - The user group to check permissions against
   * @returns boolean indicating if the user has access to the application
   */
  hasApplicationAccess(applicationName: string, userGroup?: IUserGroup): boolean {
    if (!userGroup?.applicationAccess || userGroup.applicationAccess.length === 0) {
      return false;
    }

    return userGroup.applicationAccess.includes(applicationName);
  },

  /**
   * Gets the application accessibility for a user group
   * @param userGroup - The user group to check permissions for
   * @returns IApplicationAccessibility object with APD and OnFarm access flags
   */
  getModuleAccess(userGroup?: IUserGroup): IApplicationAccessibility {
    return {
      APD: this.hasApplicationAccess(AppConstants.APPLICATION_NAMES.APD, userGroup),
      OnFarm: this.hasApplicationAccess(AppConstants.APPLICATION_NAMES.ONFARM, userGroup)
    };
  },

  /**
   * Checks if a module should be visible in the current application context
   * @param moduleName - The name of the module to check visibility for
   * @param userGroup - The user group to check permissions against
   * @param isOnFarmContext - Whether we're currently in the OnFarm application context
   * @returns boolean indicating if the module should be visible
   */
  isModuleVisibleInCurrentApp(
    moduleName: string,
    userGroup?: IUserGroup,
    isOnFarmContext: boolean = false
  ): boolean {
    if (!userGroup?.permissions || userGroup.permissions.length === 0) {
      return false;
    }

    const permission = userGroup.permissions.find(
      (p: IPermission) => p.name.toLowerCase() === moduleName.toLowerCase()
    );

    if (!permission || !permission.applications || permission.applications.length === 0) {
      return false;
    }

    const targetApp = isOnFarmContext ? AppConstants.APPLICATION_NAMES.ONFARM : AppConstants.APPLICATION_NAMES.APD;
    return permission.applications.includes(targetApp);
  },

  getWkidForLongitude(longitude: any) {
    const zoneNumber = Math.floor((Number(longitude) + 180) / 6) + 1;
    let wkid: any = null;
    AppConstants.utmZonesList.forEach((zone) => {
      if (zone.zoneNumber === zoneNumber) {
        wkid = zone.wkid;
      }
    });
    return wkid;
  },

  rgbaToHex(colorObj: any) {
    const [r, g, b] = colorObj;
    if (r == undefined || g == undefined || b == undefined) {
      return "#000000";
    }
    const rgbaHex = [r, g, b]
      .map((color) => color.toString(16).toUpperCase().padStart(2, "0"))
      .join("");
    return `#${rgbaHex}`;
  },

  hexToRgba(hex: string, alpha: number): [number, number, number, number] {
    if (hex === "") return [255, 255, 255, 1];
    const hexValue = hex.replace("#", "");
    const bigint = parseInt(hexValue, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return [r, g, b, alpha];
  },

  exportExcludeColumnsForObservationBook(
    columns: any[] = [],
    isIncludedCreatedDate: boolean = true,
  ) {
    let defaultPayload: any = {
      status: false,
      CreatedByUser: false,
      CreatedDate: false,
      ModifiedByUser: false,
      ModifiedDate: false,
    };
    if (!isIncludedCreatedDate) {
      delete defaultPayload.CreatedDate;
      // Removed CreatedDate from payload if already included in export columns
    }

    if (!Array.isArray(columns) || columns.length === 0) {
      return defaultPayload;
    }
    const isStatusExist = columns.filter(
      (item) => item?.value?.toLowerCase() === "status" && item?.checked,
    );
    // this one specific of status column only because some of export not needed
    if (isStatusExist?.length > 0) {
      const { status, ...rest } = defaultPayload;
      defaultPayload = { ...rest };
    }

    const excludeColumnsSet = columns.filter((item) => !item.checked); // sending only unchecked values
    const excludedPayload: any = { ...defaultPayload };
    for (const column of excludeColumnsSet) {
      if (column?.value) {
        excludedPayload[column.value] = column.checked;
      }
    }
    return excludedPayload;
  },

  exportExcludeColumns(
    columnFilterOptions: any[] = [],
    gridColumns: any[] = [],
    isExportWithoutFilters: boolean = false,
  ) {
    const isAllVisible = isExportWithoutFilters || !columnFilterOptions?.length;
    const checkedMap = new Map(
      columnFilterOptions.map((col) => [col.value, !!col.checked]),
    );
    const payload = gridColumns
      .filter((col) => col.dataKey)
      .map((col, idx) => ({
        ColumnName: col.dataKey,
        displayName: col.label,
        sequence: idx + 1,
        IsVisible: isAllVisible || checkedMap.get(col.dataKey) !== false,
      }));

    const defaultColumns = [
      "status",
      "CreatedByUser",
      "CreatedDate",
      "ModifiedByUser",
      "ModifiedDate",
    ];

    defaultColumns.forEach((col, i) => {
      payload.push({
        ColumnName: col,
        displayName: col,
        sequence: payload.length + 1,
        IsVisible: false,
      });
    });

    return payload;
  },
  downloadFile(data: any, headers: any) {
    if (!data) return;

    const contentDisposition = headers["content-disposition"];
    if (contentDisposition) {
      const matches = contentDisposition.match(/filename="?([^"]+)"?/);
      if (matches?.[1]) {
        console.log("matches", matches[1]);
        const blob = new Blob([data], {
          type: headers["content-type"] ?? "application/octet-stream",
        }); // Use dynamic type from headers or fallback to default

        /* Below code is used to download the file directly in fiel browser */
        // const link = document.createElement("a");
        // link.download = matches[1];
        // link.click();
        /* */

        /* Below code will open a file explorer to select a place and set the name */
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = matches[1];
        link.click();
        window.URL.revokeObjectURL(blobUrl); // Clean up after download
      }
    }
  },
  formattedDate(date: any, format: string = "MM/DD/YYYY") {
    if (!date) return "";
    return moment(date).format(format);
  },
  getColumnType(dataType: any) {
    switch (dataType.name) {
      case AttributeDatatype.LOV:
        return ColumnType.DropDown;
      case AttributeDatatype.Date:
        return ColumnType.Date;
      case AttributeDatatype.Flag:
        return ColumnType.DropDown;
      default:
        return ColumnType.TextField;
    }
  },

  async downloadFileFromURL(
    url: string,
    filename: string,
    openInNewTab: boolean = false,
  ) {
    try {
      const response = await fetch(url);
      if (response?.status !== 202 && response?.status !== 200) return false;
      const blob = await response.blob();
      const fileURL = URL.createObjectURL(blob);

      if (openInNewTab) {
        window.open(fileURL, "_blank");
      } else {
        const link = document.createElement("a");
        link.href = fileURL;
        link.download = filename; // Ensure download attribute is set
        link.style.display = "none"; // Hide the link
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(fileURL), 100); // Delay cleanup to ensure download
      }
      return true;
    } catch (error) {
      console.error("Error while downloading file", error);
      return false;
    }
  },
  sortByDate(array: any, type: string = SortType.ASC) {
    if (!array) return;
    if (type === SortType.DESC) {
      return array.sort(
        (a: any, b: any) =>
          new Date(a.modifiedAt).valueOf() - new Date(b.modifiedAt).valueOf(),
      );
    }
    return array?.sort(
      (a: any, b: any) =>
        new Date(b.modifiedAt).valueOf() - new Date(a.modifiedAt).valueOf(),
    );
  },
  parsePxValue(value: string | number) {
    if (typeof value === "number") {
      return value; // Already a number, return as is
    }
    if (typeof value === "string" && value.endsWith("px")) {
      return parseFloat(value.replace("px", ""));
    }

    return !value ? 0 : parseFloat(value); // For number-like strings without 'px'
  },

  isPlaceholderProduct(product: IProduct): boolean {
    return product?.name?.toLowerCase?.() === "xplaceholder";
  },

  sort(array: any, key: string, type: string = SortType.ASC) {
    if (!Array.isArray(array) || array.length === 0 || !key) return [];

    return array.slice().sort((a, b) => {
      const valueA = a[key];
      const valueB = b[key];

      // Handle `null`, `undefined`, and non-existing properties gracefully
      if (valueA === undefined || valueA === null)
        return type === SortType.ASC ? -1 : 1;
      if (valueB === undefined || valueB === null)
        return type === SortType.ASC ? 1 : -1;

      // Handle string comparison
      if (typeof valueA === "string" && typeof valueB === "string") {
        return type === SortType.ASC
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }

      // TO DO: add a condition to check for date types as well
      // Handle numeric values and other types directly
      return Math.sign(
        type === SortType.ASC ? valueA - valueB : valueB - valueA,
      );
    });
  },
  isFillerSubstituted(row: any) {
    if (!row) return;
    return row?.isFiller === true && !row?.productId;
  },
  getReorderedColumns(reorderList: string[], columns: any[]) {
    if (!Array.isArray(columns)) {
      return [];
    }
    if (!reorderList || reorderList.length === 0) {
      return columns;
    }

    const columnMap = new Map(columns.map((col) => [col.dataKey, col]));
    const reorderedCols = reorderList
      .map((key) => columnMap.get(key))
      .filter((col): col is any => !!col);
    // Ensure all reordered columns are present in the original list if they not exist then will be added at the end
    const reorderedKeys = new Set(reorderList);
    const remainingCols = columns.filter(
      (col) => !reorderedKeys.has(col.dataKey),
    );

    return [...reorderedCols, ...remainingCols];
  },
  getPreferencesPayload(
    appliedFilters: any[],
    columnFilterOptions: any[],
    gridColumns: any[],
    appliedSorting: any[] = [],
    appliedGlobalFilter?: {},
    seasonType?: string
  ) {
    const preferenceFilters =
      appliedFilters.map(
        ({
          dataKey,
          value,
          queryParamsKey,
          inputType,
          operatorType = "",
          connector = "",
          inputField = "",
        }) => {
          return {
            dataKey,
            queryParamsKey,
            inputType,
            value,
            operatorType,
            connector,
            inputField,
          };
        },
      ) ?? [];
    // Use appliedSorting directly since it already contains only dataKey and sortOrder
    const preferenceSorting = appliedSorting ?? [];

    const hideColumns =
      columnFilterOptions
        .filter((item) => !item.checked)
        .map((item) => item.value) ?? [];
    const columnsOrder = gridColumns.map((item) => item.dataKey) ?? [];

    const payload: any = {
      gridHideColumns: hideColumns,
      gridFilters: preferenceFilters,
      gridSorting: preferenceSorting,
      columnsOrder: columnsOrder,
      appliedGlobalFilter: appliedGlobalFilter,
      ...(seasonType && { seasonType }),
    };

    return payload;
  },

  getRecordCountText(
    totalCount: number,
    filteredCount: number,
    isFilterCount: boolean = false,
  ): string {
    return `Total Records: ${isFilterCount ? `${filteredCount} of ` : ""
      }${totalCount}`;
  },

  getLogicalOperatorString(operatorType: string): string {
    return operatorType === "AND" ? `;` : `|`;
  },

  getOperatorValue(operatorType: string): string {
    // TO DO:  this will be referred from app.enum.ts created by Gourav
    const operatorTypeMap = [
      {
        label: "Equal",
        value: "eq",
        operationType: ["numeric", "text", "date"],
      },
      {
        label: "Not Equal",
        value: "noteq",
        operationType: ["numeric", "text", "date"],
      },
      {
        label: "Greater Than",
        value: "gt",
        operationType: ["numeric", "date"],
      },
    ];
    const operatorData = operatorTypeMap.find(
      (item) => item.label === operatorType,
    );
    return operatorData?.value ?? operatorType;
  },

  //  {host}/trialMaster/v1/inventory?Filter=Name__eq:=Inv1;Quantity__in:=10,40,50|Lot__noteq:=9897
  // https://localhost:7257/brand?filter=CreatedDate__bt:=2023-01-01,2025-12-31|id__in:=1,2,3;modifiedDate__gt:=2023-01-01&sort=Name-,CreatedDate
  // ; denotes AND
  // | denotes OR
  // := --> separates variable expression from value
  // __ --> separates variable from operation

  createFilterQueryParams(
    queryParamsInfoList: Array<IFilterQueryParams>,
  ): string {
    if (
      !Array.isArray(queryParamsInfoList) ||
      queryParamsInfoList.length === 0
    ) {
      return "";
    }
    const queryParamString = queryParamsInfoList
      .map((queryParamInfo, index) => {
        const encodedValue = Array.isArray(queryParamInfo.value)
          ? queryParamInfo.value
            .map((v) => encodeURIComponent(String(v)))
            .join(",")
          : encodeURIComponent(String(queryParamInfo.value));

        const paramString = `${queryParamInfo.dataKey}__${this.getOperatorValue(
          queryParamInfo.operatorType,
        )}:=${encodedValue}`;
        const connector =
          index > 0 && queryParamInfo.connector
            ? this.getLogicalOperatorString(queryParamInfo.connector)
            : "";
        return `${connector}${paramString}`;
      })
      .join("");
    return `${queryParamString}`;
  },

  GetCropNamesFromFilterQueryParams(
    queryParamsInfoList: Array<IFilterQueryParams>,
  ): string[] {
    // Find the crop name filter param (could be string or array)
    const cropNameFilter = queryParamsInfoList.find(
      (item) => item.dataKey === AgTechQueryParam.CropNameKey,
    );

    if (!cropNameFilter || !cropNameFilter.value) return [];

    // Normalize to array (handles string, array, or empty)
    if (Array.isArray(cropNameFilter.value)) {
      return cropNameFilter.value;
    }
    if (typeof cropNameFilter.value === "string") {
      // Split by comma if needed, trim spaces
      return cropNameFilter.value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [];
  },

  createSortQueryParams(queryParamsInfoList: Array<ISortQueryParams>): string {
    if (
      !Array.isArray(queryParamsInfoList) ||
      queryParamsInfoList.length === 0
    ) {
      return "";
    }
    const queryParamString = queryParamsInfoList
      .map((queryParamInfo, index) => {
        const sortOrder = queryParamInfo.sortOrder === "desc" ? "-" : "";
        const sortConnector = index > 0 ? "," : "";
        return `${sortConnector}${queryParamInfo.dataKey}${sortOrder}`;
      })
      .join("");

    return `${queryParamString}`;
  },

  emptyStringToNull(val: string | null | undefined): string | null {
    return val === "" ? null : (val ?? null);
  },

  splitValues(value: string | null | undefined): string[] {
    if (!value) return [];
    return value.split(",").map((item) => item.trim());
  },

  getDefaultSortOrderByKey(
    keyName: AgTechQueryParam,
    sortOrder: SortType = SortType.DESC,
  ): { dataKey: AgTechQueryParam; sortOrder: SortType }[] {
    return keyName ? [{ dataKey: keyName, sortOrder }] : [];
  },

  isPreferenceApplied(preferenceInfo: any) {
    if (!preferenceInfo || preferenceInfo.length === 0) return true;
    const {
      gridFilters = [],
      gridHideColumns = [],
      gridSorting = [],
      columnsOrder = [],
    } = preferenceInfo;

    // Return true only if ALL arrays are empty
    return (
      gridFilters.length === 0 &&
      gridHideColumns.length === 0 &&
      gridSorting.length === 0 &&
      columnsOrder.length === 0
    );
  },

  formattedDateTime(selectedDate: Date | string): string {
    return moment(selectedDate).format(); // returns the local timezone format if no format is specified
  },

  convertFeetToMeter(feetValue: number) {
    return feetValue * 0.3048;
  },

  convertUtcToLocalTime(
    selectedDate: Date | string,
    format: string = "MM/DD/YYYY hh:mm A",
  ): string {
    if (!selectedDate) return "";
    return moment.utc(selectedDate).local().format(format);
  },
  parseFormattedDateTime(
    selectedDate: Date | string,
    format: string = "MM/DD/YYYY hh:mm A",
  ): string {
    if (!selectedDate) return "";
    /**
     * Uses moment.parseZone to preserve the original timezone offset
     * from the input date instead of converting it to local time.
     * Useful when displaying server-provided timestamps exactly as sent.
     */
    return moment.parseZone(selectedDate).format(format);
  },
  appendCurrentTimeToDate(selectedDate: Date | string) {
    const now = new Date();
    const dateObj = new Date(selectedDate);
    dateObj.setHours(
      now.getHours(),
      now.getMinutes(),
      now.getSeconds(),
      now.getMilliseconds(),
    );
    return dateObj;
  },
  getDefaultNullValue(selectedOption: any) {
    if (selectedOption?.isNullable) return "";
    switch (selectedOption.dataType) {
      case AttributeDatatype.Number: {
        return 0;
      }
      case AttributeDatatype.Flag: {
        return false;
      }
      case AttributeDatatype.Date: {
        return "0000-00-00T00:00:00";
      }
      default: {
        return "";
      }
    }
  },
  roundToTwoDecimals(num: number): number {
    if (typeof num !== "number") return num; // return as is if not a number
    return Math.round(num * 100) / 100;
  },
  downloadExcelFile: async (
    workbook: ExcelJS.Workbook,
    fileName: string,
  ): Promise<void> => {
    // Write file to buffer
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },
  handleArrowKeyNavigation(
    e: React.KeyboardEvent,
    row: number,
    col: number,
    maxRows: number,
    focusInput: (row: number, col: number) => void,
  ) {
    if (e.key === "ArrowUp" && row > 0) {
      focusInput(row - 1, col);
      e.preventDefault();
    } else if (e.key === "ArrowDown" && row < maxRows - 1) {
      focusInput(row + 1, col);
      e.preventDefault();
    }
  },
  getModifiedInputValue(isNumber: boolean, value: string): string {
    if (isNumber && (value === "" || value === "0")) {
      return "";
    }
    return value ?? "";
  },
  getDemoSubTypeOptionsBasedOnDemoTypeId(
    demoSubTypeAllList: any[],
    parentDemoTypeId: string,
  ) {
    if (!parentDemoTypeId) return demoSubTypeAllList ?? [];
    return (
      demoSubTypeAllList.filter(
        (subType: any) => subType?.parentlovId?.toString() === parentDemoTypeId?.toString(),
      ) ?? []
    );
  },
  getSubDemoTypeOptions(
    attributesList: any[] | undefined,
    demoType: string,
    demoTypeKey: string,
    demoSubTypeKey: string,
  ) {
    if (!demoType || !attributesList || !demoTypeKey || !demoSubTypeKey)
      return [];

    const demoTypeAttribute = attributesList.find(
      (attr: any) => attr.dataKey === demoTypeKey,
    );

    const selectedDemoType = demoTypeAttribute?.options?.find(
      (option: any) => option.value === demoType,
    );

    if (!selectedDemoType) return [];

    const demoSubTypeAttribute = attributesList.find(
      (attr: any) => attr.dataKey === demoSubTypeKey,
    );

    return (
      demoSubTypeAttribute?.options?.filter(
        (option: any) => option.parentlovId === selectedDemoType.id,
      ) ?? []
    );
  },
  getPreviousYearsDate(count = 1): Date {
    const date = new Date();
    date.setFullYear(date.getFullYear() - count);
    return date;
  },
  tofixedDecimalValue(num: number, decimalPlaces: number = 2): number | string {
    if (typeof num !== "number") return num;
    return Number.isInteger(num) ? num : num.toFixed(decimalPlaces);
  },
  isVisibleAttributes(attributes: any) {
    if (attributes?.length === 0) return [];
    return attributes?.filter((attr: any) => attr?.isVisible);
  },
  sortBySelected<T>(
    list: T[],
    selected: string[] = [],
    getName: (item: T) => string,
  ): T[] {
    return [...list].sort((a, b) => {
      const aSelected = selected.includes(getName(a));
      const bSelected = selected.includes(getName(b));

      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    });
  },

  isOnFarm(): boolean {
    try {
      // Read current application from localStorage
      const currentApplication = localStorage.getItem('setCurrentApplication');
      return currentApplication === AppConstants.APPLICATION_CODES.ONFARM;
    } catch {
      return false;
    }
  },

  getFormattedDateForReports(date: Date = new Date()): string {
    return moment(date).format("YYYY/MM/DD");
  },
  isUncheckColumnExist(columns: any[] = []): boolean {
    const isUncheckColumn = columns.some((col) => col.checked === false);
    return isUncheckColumn ?? false;
  },
  getValueByPath(obj: any, path: string): any {
    if (!obj || !path) return null;
    const result = path
      .split('.')
      .reduce((current, key) => current?.[key], obj);
    return result ?? null;
  },
  filterUserAccessibleMenus(
    isKioskMode: boolean,
    navItems: any[],
    userRole: string = "",
    permissions: IPermission[] = [],
    isAdmin: boolean = false,
    isOnFarmView = false
  ) {
    let filtered = navItems;
    // for admin we are not applying permission check as they have access to all the modules, for non admin users we will filter the nav items based on permissions
    if (!isAdmin) {
      // Filter navItems based on permissions for non-admin users
      filtered = filtered.filter((item) => {
        // If moduleKeyName does not exist, do not filter out the item
        if (!item?.moduleKeyName) {
          return true;
        }
        const permission = permissions.find(
          (perm) =>
            perm?.name?.toLowerCase() === item?.moduleKeyName?.toLowerCase()
        );
        return permission?.canView === true;
      });
    }
    // Filter based on kiosk mode
    if (isKioskMode) {
      filtered = filtered.filter((item) => item.isKioskAccessible);
    }

    let selectedApplication = AppConstants.APPLICATION_NAMES.APD; // Default to APD
    if (isOnFarmView) {
      selectedApplication = AppConstants.APPLICATION_NAMES.ONFARM;
    }
    filtered = filtered.filter((item) => item.applicationsAccess?.includes(selectedApplication));

    // THIS NEEDS TO BE HANDLED FROM BACKEND, LATER THE 19th FEB RELEASE.
    // Filter based on Master Catalog access
    filtered = filtered.filter((item) => {
      if (item.isMasterCatalog) {
        // Master Catalog is accessible to System Admin, Field Admin, and Kiosk users
        const allowedRoles = MasterCatalogAllowedRoles;
        return allowedRoles.includes(userRole as UserRoles);
      }
      return true;
    });

    return filtered;
  },

  getMasterCatalogAttribute(item: any, row: any): any {
    const dataKey = item?.dataKey;

    if (!item?.isCollectionData) {
      return row?.[dataKey] ?? "";
    }

    if (!dataKey) return "";

    for (const prefix in ATTRIBUTE_MASTER_CATALOG_PREFIX_MAP) {
      if (dataKey.startsWith(prefix)) {
        return (
          row?.[ATTRIBUTE_MASTER_CATALOG_PREFIX_MAP[prefix]]?.[
          dataKey.slice(prefix.length)
          ] ?? ""
        );
      }
    }

    return row?.attributes?.[dataKey] ?? "";
  },
  sortStrings(items: string[], order: "asc" | "desc" = "asc"): string[] {
    const sorted = [...items].sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase()),
    );
    return order === "desc" ? sorted.reverse() : sorted;
  },
  isColumnOrderDifferent(
    modifiedList: { dataKey: string }[] = [],
    originalColumnsList: { dataKey: string }[] = []
  ): boolean {
    return (
      modifiedList.length !== originalColumnsList.length ||
      modifiedList.some(
        (attr, i) => attr.dataKey !== originalColumnsList[i]?.dataKey
      )
    );
  }

};
